import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, get } from "firebase/database";
import { db } from "../firebase";

// Helper for local date to avoid timezone bugs
function getLocalDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function ReportsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [areaId, setAreaId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Date states
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return getLocalDateString(date);
  });
  const [toDate, setToDate] = useState(() => getLocalDateString());
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [generateClicked, setGenerateClicked] = useState(false);
  
  // Local storage caching states
  const [cachedEntries, setCachedEntries] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const printRef = useRef();

  // Fetch area metadata and customers
  useEffect(() => {
    const fetchAreaData = async () => {
      setLoading(true);
      try {
        const areasRef = ref(db, "areas");
        const snapshot = await get(areasRef);
        const val = snapshot.val() || {};
        
        const found = Object.entries(val).find(
          ([, v]) => v.meta && v.meta.slug === slug
        );
        
        if (found) {
          const aId = found[0];
          setAreaId(aId);
          setMeta(found[1].meta);
          
          const customersData = found[1].customers || {};
          const customersArray = Object.entries(customersData).map(([id, c]) => ({
            id,
            ...c
          }));
          customersArray.sort((a, b) => a.name.localeCompare(b.name));
          setCustomers(customersArray);

          // Load cached entries from localStorage for this specific date range
          const cacheKey = `entries_${aId}_${fromDate}_${toDate}`;
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            setCachedEntries(parsed.data);
            setLastUpdated(parsed.timestamp);
          }
        }
      } catch (err) {
        console.error("Error:", err);
        alert("Error loading data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchAreaData();
  }, [slug]); // Removed fromDate/toDate from deps to avoid re-fetching area on date change

  // Function to download ONLY the selected date range
  const downloadAllEntries = async () => {
    if (!areaId || !fromDate || !toDate) return;
    setIsFetchingData(true);
    
    try {
      // Create date range array
      const startDate = new Date(fromDate);
      const endDate = new Date(toDate);
      const dateRange = [];
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        dateRange.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Fetch only the specific days in the range
      const entryPromises = dateRange.map(date => {
        const entriesRef = ref(db, `areas/${areaId}/entries/${date}`);
        return get(entriesRef);
      });
      
      const snapshots = await Promise.all(entryPromises);
      
      const downloadedData = {};
      snapshots.forEach((snapshot, index) => {
        const dayData = snapshot.val();
        if (dayData) {
          downloadedData[dateRange[index]] = dayData;
        }
      });
      
      // Save to localStorage (this will be much smaller and faster)
      const cacheKey = `entries_${areaId}_${fromDate}_${toDate}`;
      const cacheData = {
        data: downloadedData,
        timestamp: Date.now()
      };
      
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (storageErr) {
        console.warn("LocalStorage full, clearing old report cache...");
        // If storage is full, clear old caches and try again
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(`entries_${areaId}`)) localStorage.removeItem(key);
        });
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      }
      
      setCachedEntries(downloadedData);
      setLastUpdated(cacheData.timestamp);
      
      alert("✅ Data updated successfully! Now using local storage.");
    } catch (err) {
      console.error("Error fetching entries:", err);
      alert("❌ Error downloading data.");
    } finally {
      setIsFetchingData(false);
    }
  };

  // Calculate report using CACHED data (No Firebase charges)
  const calculateReport = useCallback(() => {
    if (!cachedEntries) {
      alert("⚠️ Please click 'Update Data' first to download the entries.");
      return;
    }
    if (!selectedCustomer || !fromDate || !toDate) return;
    
    setCalculating(true);
    setReportData(null);
    setGenerateClicked(true);
    
    try {
      const customer = customers.find(c => c.id === selectedCustomer);
      if (!customer) {
        alert("Customer not found!");
        setCalculating(false);
        return;
      }
      
      const allEntries = [];
      let totalOut = 0;
      let totalIn = 0;
      let totalPaid = 0;
      
      // Loop through cached dates and filter by customer and date range
      Object.entries(cachedEntries).forEach(([entryDate, dayEntries]) => {
        // Double check date is within range
        if (entryDate >= fromDate && entryDate <= toDate) {
          Object.values(dayEntries).forEach(entry => {
            if (entry.customerId === selectedCustomer) {
              const out = Number(entry.out) || 0;
              const in_ = Number(entry.in) || 0;
              const paid = Number(entry.paid) || 0;
              
              allEntries.push({
                ...entry,
                date: entryDate
              });
              
              totalOut += out;
              totalIn += in_;
              totalPaid += paid;
            }
          });
        }
      });
      
      // Sort by date
      allEntries.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      const pricePerJar = Number(customer.price) || 0;
      const expectedAmount = totalOut * pricePerJar;
      
      let pendingAmount = 0;
      let advanceAmount = 0;
      
      if (totalPaid < expectedAmount) {
        pendingAmount = expectedAmount - totalPaid;
      } else if (totalPaid > expectedAmount) {
        advanceAmount = totalPaid - expectedAmount;
      }
      
      const netJars = totalOut - totalIn;
      const pendingJars = Math.max(0, netJars);
      
      setReportData({
        entries: allEntries,
        totals: {
          out: totalOut,
          in: totalIn,
          netJars: netJars,
          paid: totalPaid,
          expected: expectedAmount,
          pendingAmount: pendingAmount,
          advanceAmount: advanceAmount,
          pendingJars: pendingJars
        },
        customer: customer,
        dateRange: { from: fromDate, to: toDate }
      });
      
    } catch (err) {
      console.error("Error:", err);
      alert("Error calculating report");
    } finally {
      setCalculating(false);
    }
  }, [cachedEntries, selectedCustomer, fromDate, toDate, customers]);

  // Clear cache when dates change
  useEffect(() => {
    if (areaId) {
      const cacheKey = `entries_${areaId}_${fromDate}_${toDate}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        setCachedEntries(parsed.data);
        setLastUpdated(parsed.timestamp);
      } else {
        setCachedEntries(null);
        setLastUpdated(null);
      }
    }
  }, [fromDate, toDate, areaId]);

  const handlePrint = () => {
    // ... (Keep your existing handlePrint function exactly as it is) ...
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Customer Report - ${reportData?.customer?.name}</title>
          <meta charset="UTF-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; padding: 20px; font-size: 12px; }
            .print-container { max-width: 100%; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #000; }
            .company-name { font-size: 22px; font-weight: bold; margin-bottom: 5px; }
            .report-title { font-size: 16px; margin-bottom: 10px; }
            .customer-info { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin: 15px 0; padding: 10px; border: 1px solid #000; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #000; padding: 8px; text-align: center; }
            th { background: #f2f2f2; font-weight: bold; }
            .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
            .summary-card { border: 1px solid #000; padding: 10px; }
            .summary-card h4 { margin-bottom: 10px; border-bottom: 1px solid #000; padding-bottom: 5px; }
            .summary-item { display: flex; justify-content: space-between; margin: 5px 0; }
            .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px solid #000; font-size: 10px; }
            .pending { color: #dc3545; }
            .advance { color: #28a745; }
            .page-break { page-break-before: always; margin-top: 20px; }
            @media print { body { padding: 0; } .page-break { page-break-before: always; } }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent}
          </div>
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 500);
            };
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExportCSV = () => {
    // ... (Keep your existing handleExportCSV function exactly as it is) ...
    if (!reportData) return;
    
    const headers = ['Date', 'Jars Out', 'Jars In', 'Net Jars', 'Amount Paid (₹)', 'Price/Jar (₹)', 'Expected (₹)'];
    const rows = reportData.entries.map(entry => {
      const expected = (entry.out || 0) * reportData.customer.price;
      return [
        entry.date,
        entry.out || 0,
        entry.in || 0,
        (entry.out || 0) - (entry.in || 0),
        entry.paid || 0,
        reportData.customer.price,
        expected.toFixed(2)
      ];
    });
    
    rows.push(['', '', '', '', '', '', '']);
    rows.push(['TOTAL', reportData.totals.out, reportData.totals.in, reportData.totals.netJars, reportData.totals.paid.toFixed(2), '', reportData.totals.expected.toFixed(2)]);
    rows.push(['', '', '', '', '', '', '']);
    rows.push(['PENDING AMOUNT:', '', '', '', reportData.totals.pendingAmount.toFixed(2), '', '']);
    rows.push(['ADVANCE AMOUNT:', '', '', '', reportData.totals.advanceAmount.toFixed(2), '', '']);
    rows.push(['PENDING JARS:', '', '', '', reportData.totals.pendingJars, '', '']);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportData.customer.name}_report_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isGenerateDisabled = !selectedCustomer || !fromDate || !toDate || calculating || !cachedEntries;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div className="spinner"></div>
        <p>Loading customers...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h2>Customer Reports — {meta?.name}</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {lastUpdated && (
            <span style={{ fontSize: '12px', color: '#666' }}>
              Data cached: {new Date(lastUpdated).toLocaleString()}
            </span>
          )}
          <button 
            onClick={downloadAllEntries} 
            disabled={isFetchingData || !fromDate || !toDate}
            style={{
              padding: '10px 20px',
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (isFetchingData || !fromDate || !toDate) ? 'not-allowed' : 'pointer'
            }}
          >
            {isFetchingData ? '⏳ Updating...' : '🔄 Update Data for Range'}
          </button>
          <button 
            onClick={() => navigate(-1)} 
            style={{
              padding: '10px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
        </div>
      </div>

      {!cachedEntries && !isFetchingData && (
        <div style={{ background: '#fff3cd', color: '#856404', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          ⚠️ <strong>No local data found for this date range.</strong> Please click <strong>"🔄 Update Data for Range"</strong> to download these specific dates. Once downloaded, generating reports will be FREE and instant.
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select Customer:</label>
            <select
              value={selectedCustomer || ""}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setGenerateClicked(false);
              }}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            >
              <option value="">-- Select Customer --</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} ({customer.type === 'cooljar' ? 'Cool Jar' : 'Jar'} - ₹{customer.price})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>From Date:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setGenerateClicked(false);
              }}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>To Date:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setGenerateClicked(false);
              }}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={calculateReport}
              disabled={isGenerateDisabled}
              style={{
                width: '100%',
                padding: '10px 20px',
                background: isGenerateDisabled ? '#ccc' : '#9b0033',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isGenerateDisabled ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              {calculating ? 'Generating...' : '📊 Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {calculating && (
        <div style={{ textAlign: 'center', padding: '50px', background: 'white', borderRadius: '8px' }}>
          <div className="spinner"></div>
          <p>Generating report for selected dates...</p>
        </div>
      )}

      {!calculating && !generateClicked && selectedCustomer && fromDate && toDate && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px dashed #dee2e6'
        }}>
          <p style={{ fontSize: '16px', color: '#6c757d' }}>
            👆 Click "Generate Report" to view customer statement for selected date range
          </p>
        </div>
      )}

      {reportData && !calculating && generateClicked && (
        <>
          {/* Report Content for Printing */}
          <div ref={printRef}>
            {/* Page 1 - Transactions */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #eee' }}>
                <h1 style={{ color: '#9b0033', marginBottom: '5px' }}>Jauhari Enterprises</h1>
                <h3>Customer Statement Report</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '10px',
                  marginTop: '15px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <div><strong>Customer:</strong> {reportData.customer.name}</div>
                  <div><strong>Area:</strong> {meta?.name}</div>
                  <div><strong>Type:</strong> {reportData.customer.type === 'cooljar' ? 'Cool Jar' : 'Jar'}</div>
                  <div><strong>Mode:</strong> {reportData.customer.mode?.toUpperCase()}</div>
                  <div><strong>Price/Jar:</strong> ₹{reportData.customer.price}</div>
                  <div><strong>Period:</strong> {fromDate} to {toDate}</div>
                </div>
              </div>

              {/* Transactions Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>S.No</th>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>Jars Out</th>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>Jars In</th>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>Net Jars</th>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>Amount Paid (₹)</th>
                      <th style={{ border: '1px solid #ddd', padding: '10px' }}>Expected (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.entries.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No transactions found for selected period</td>
                      </tr>
                    ) : (
                      reportData.entries.map((entry, index) => {
                        const netJars = (entry.out || 0) - (entry.in || 0);
                        const expected = (entry.out || 0) * reportData.customer.price;
                        return (
                          <tr key={index}>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{entry.date}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{entry.out || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{entry.in || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', color: netJars > 0 ? '#dc3545' : '#28a745' }}>
                              {netJars}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>₹{(entry.paid || 0).toFixed(2)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>₹{expected.toFixed(2)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="2" style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}><strong>TOTAL</strong></td>
                      <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}><strong>{reportData.totals.out}</strong></td>
                      <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}><strong>{reportData.totals.in}</strong></td>
                      <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}><strong>{reportData.totals.netJars}</strong></td>
                      <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}><strong>₹{reportData.totals.paid.toFixed(2)}</strong></td>
                      <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}><strong>₹{reportData.totals.expected.toFixed(2)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Page 2 - Summary */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '8px', pageBreakBefore: 'always' }}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Summary Report</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                {/* Jars Summary */}
                <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '15px', borderBottom: '2px solid #9b0033', paddingBottom: '5px' }}>📊 Jars Summary</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Total Jars Out:</span>
                    <strong>{reportData.totals.out}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Total Jars In:</span>
                    <strong>{reportData.totals.in}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Net Jars Consumed:</span>
                    <strong>{reportData.totals.netJars}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span>Pending Jars:</span>
                    <strong style={{ color: '#dc3545' }}>{reportData.totals.pendingJars}</strong>
                  </div>
                </div>

                {/* Financial Summary */}
                <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '15px', borderBottom: '2px solid #9b0033', paddingBottom: '5px' }}>💰 Financial Summary</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Total Amount Paid:</span>
                    <strong>₹{reportData.totals.paid.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Expected Amount:</span>
                    <strong>₹{reportData.totals.expected.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span>Difference:</span>
                    <strong style={{ color: reportData.totals.pendingAmount > 0 ? '#dc3545' : '#28a745' }}>
                      {reportData.totals.pendingAmount > 0 ? `Pending: ₹${reportData.totals.pendingAmount.toFixed(2)}` : `Advance: ₹${reportData.totals.advanceAmount.toFixed(2)}`}
                    </strong>
                  </div>
                </div>

                {/* Current Status */}
                <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '15px', borderBottom: '2px solid #9b0033', paddingBottom: '5px' }}>📌 Current Status</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Pending Amount:</span>
                    <strong style={{ color: '#dc3545' }}>₹{reportData.totals.pendingAmount.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Advance Amount:</span>
                    <strong style={{ color: '#28a745' }}>₹{reportData.totals.advanceAmount.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span>Security Deposit:</span>
                    <strong>₹{reportData.customer.securityMoney || 0}</strong>
                  </div>
                </div>
              </div>

              {/* Payment Status Banner */}
              <div style={{
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
                marginTop: '20px',
                background: reportData.totals.pendingAmount > 0 ? '#fff3cd' : (reportData.totals.advanceAmount > 0 ? '#d4edda' : '#cce5ff'),
                border: `1px solid ${reportData.totals.pendingAmount > 0 ? '#ffc107' : (reportData.totals.advanceAmount > 0 ? '#28a745' : '#004085')}`
              }}>
                {reportData.totals.pendingAmount > 0 ? (
                  <>
                    <strong style={{ fontSize: '18px', color: '#dc3545' }}>⚠️ PENDING PAYMENT</strong>
                    <p style={{ marginTop: '10px', fontSize: '16px' }}>
                      Customer needs to pay: <strong style={{ fontSize: '24px', color: '#dc3545' }}>₹{reportData.totals.pendingAmount.toFixed(2)}</strong>
                    </p>
                    <p>Pending Jars: {reportData.totals.pendingJars}</p>
                  </>
                ) : reportData.totals.advanceAmount > 0 ? (
                  <>
                    <strong style={{ fontSize: '18px', color: '#28a745' }}>✅ ADVANCE PAYMENT</strong>
                    <p style={{ marginTop: '10px', fontSize: '16px' }}>
                      Customer has advance: <strong style={{ fontSize: '24px', color: '#28a745' }}>₹{reportData.totals.advanceAmount.toFixed(2)}</strong>
                    </p>
                  </>
                ) : (
                  <>
                    <strong style={{ fontSize: '18px', color: '#004085' }}>✓ SETTLED</strong>
                    <p style={{ marginTop: '10px' }}>All payments are up to date</p>
                  </>
                )}
              </div>

              <div style={{ textAlign: 'center', marginTop: '30px', paddingTop: '10px', borderTop: '1px solid #ddd', fontSize: '11px', color: '#666' }}>
                <p>This is a computer generated report - No signature required</p>
                <p>Generated on: {new Date().toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '20px' }}>
            <button onClick={handlePrint} style={{
              padding: '12px 24px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}>
              🖨️ Print Report
            </button>
            <button onClick={handleExportCSV} style={{
              padding: '12px 24px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}>
              📥 Export CSV
            </button>
          </div>
        </>
      )}

      <style>{`
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #9b0033;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media print {
          .spinner, button, .filters-card, .action-buttons {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}